import { useState } from 'react'
import axios from 'axios'
import apiClient from '../api/client'
import styles from './CreateCampaignPage.module.css'

interface GeneratedCampaign {
  id?: string
  product: string
  audience: string
  platform: string
  budget: string
  strategy: string
  performance_score: number
  headline: string
  primary_text: string
  call_to_action: string
  image_url?: string
  scheduled_time?: string
}

function toDatetimeLocalValue(date: Date): string {
  const offsetMs = date.getTimezoneOffset() * 60000
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16)
}

function getDefaultScheduleValue(): string {
  const nextRun = new Date()
  nextRun.setDate(nextRun.getDate() + 1)
  nextRun.setHours(9, 0, 0, 0)
  return toDatetimeLocalValue(nextRun)
}

function toFacebookStartTime(value: string): string | undefined {
  if (!value) return undefined
  return new Date(value).toISOString()
}

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail
    if (typeof detail === 'string') return detail
    if (detail?.message) return detail.message
    return error.message
  }
  return error instanceof Error ? error.message : 'Something went wrong'
}

export function CreateCampaignPage() {
  const [product, setProduct] = useState('')
  const [goal, setGoal] = useState('Increase Sales')
  const [tone, setTone] = useState('Professional')
  const [location, setLocation] = useState('')
  const [linkUrl, setLinkUrl] = useState('https://example.com')
  const [dailyBudget, setDailyBudget] = useState(100)  // stored in ₹, sent as paise (×100) to Facebook
  const [autoSchedule, setAutoSchedule] = useState(true)
  const [scheduledTime, setScheduledTime] = useState(getDefaultScheduleValue)

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [uploading, setUploading] = useState(false)

  const [result, setResult] = useState<GeneratedCampaign | null>(null)
  const [savedId, setSavedId] = useState<string | null>(null)
  const [imageUrl, setImageUrl] = useState('')
  const [imageFileName, setImageFileName] = useState<string | null>(null)

  const [message, setMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // ── Generate Campaign ──────────────────────────
  async function generateCampaign() {
    if (!product.trim()) { setErrorMessage('Enter product name'); return }
    try {
      setLoading(true)
      setErrorMessage(null)
      setMessage(null)
      setSavedId(null)

      const response = await apiClient.post('/generate-ad', {
        product,
        goal,
        tone,
        business: product,
        location: location || 'India',
      })

      setResult(response.data)
      if (response.data?.scheduled_time) {
        setScheduledTime(toDatetimeLocalValue(new Date(response.data.scheduled_time)))
        setAutoSchedule(true)
      }
      setMessage('Campaign generated successfully')
    } catch (error) {
      setErrorMessage(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  // ── Save Campaign to DynamoDB ──────────────────
  async function saveCampaign() {
    if (!result) return
    try {
      setSaving(true)
      setErrorMessage(null)
      const response = await apiClient.post('/campaigns', {
        product: result.product,
        headline: result.headline,
        primary_text: result.primary_text,
        call_to_action: result.call_to_action,
        audience: typeof result.audience === 'object' ? JSON.stringify(result.audience) : result.audience,
        platform: result.platform,
        budget: result.budget,
        strategy: result.strategy,
        performance_score: result.performance_score,
        image_url: imageUrl,
        link_url: linkUrl,
        daily_budget: dailyBudget,
        scheduled_time: autoSchedule ? toFacebookStartTime(scheduledTime) : undefined,
        status: 'draft',
      })
      setSavedId(response.data.id)
      setResult({ ...result, id: response.data.id, scheduled_time: response.data.scheduled_time })
      setMessage('Campaign saved successfully')
    } catch (error) {
      setErrorMessage(getErrorMessage(error))
    } finally {
      setSaving(false)
    }
  }

  // ── Upload Poster Image ────────────────────────
  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      setUploading(true)
      setErrorMessage(null)
      // Step 1: get presigned S3 PUT URL from backend (uses /get-upload-url which is on the working campaign_routes router)
      const presignRes = await apiClient.post('/get-upload-url', {
        filename: file.name,
        content_type: file.type || 'image/jpeg',
      })
      const { upload_url, image_url } = presignRes.data
      // Step 2: PUT file directly to S3 — bypasses API Gateway entirely
      const s3Res = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'image/jpeg' },
        body: file,
      })
      if (!s3Res.ok) throw new Error('S3 upload failed')
      setImageUrl(image_url)
      setImageFileName(file.name)
      setMessage('Image uploaded successfully')
    } catch (error) {
      setErrorMessage(getErrorMessage(error))
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  // ── Open Poster Studio ─────────────────────────
  function openPosterStudio() {
    if (!result) { setErrorMessage('Generate campaign first'); return }
    window.open('https://poster.digitransolutions.in/', '_blank')
  }

  // ── Publish to Facebook ────────────────────────
  async function publishCampaign() {
    if (!result) return
    if (!linkUrl.trim() || linkUrl === 'https://example.com') {
      setErrorMessage('Enter your product/store URL before publishing')
      return
    }
    const facebookStartTime = autoSchedule ? toFacebookStartTime(scheduledTime) : undefined
    if (autoSchedule && (!facebookStartTime || new Date(facebookStartTime) <= new Date())) {
      setErrorMessage('Choose a future date and time for auto scheduling')
      return
    }
    try {
      setPublishing(true)
      setErrorMessage(null)
      const resp = await apiClient.post('/publish-facebook', {
        campaign_id: savedId || result.id,
        campaign_name: result.headline || `${result.product} Campaign`,
        headline: result.headline,
        primary_text: result.primary_text,
        image_url: imageUrl || undefined,
        link_url: linkUrl,
        daily_budget: dailyBudget * 100,  // convert ₹ to paise for Facebook API
        scheduled_time: facebookStartTime,
        status: 'ACTIVE',
      })
      if (resp.data.partial) {
        // Campaign + Ad Set created but Ad failed — show warning with retry option
        setErrorMessage(resp.data.warning || 'Campaign created but ad was not created. Authenticate in Ads Manager then click Retry.')
      } else {
        setMessage(autoSchedule ? 'Campaign scheduled on Facebook successfully' : 'Campaign published to Facebook successfully')
      }
    } catch (error) {
      setErrorMessage(getErrorMessage(error))
    } finally {
      setPublishing(false)
    }
  }

  // ── Retry Ad Creation ──────────────────────────
  async function retryCreateAd() {
    try {
      setPublishing(true)
      setErrorMessage(null)
      await apiClient.post('/retry-create-ad', {})
      setMessage('Ad created successfully! Campaign is now complete on Facebook.')
    } catch (error) {
      setErrorMessage(getErrorMessage(error))
    } finally {
      setPublishing(false)
    }
  }

  // ── Delete Campaign ────────────────────────────
  async function deleteCampaign() {
    if (!savedId && !result?.id) { setErrorMessage('No saved campaign to delete'); return }
    try {
      await apiClient.delete(`/campaigns/${savedId || result?.id}`)
      setResult(null)
      setImageUrl('')
      setSavedId(null)
      setMessage('Campaign deleted')
    } catch (error) {
      setErrorMessage(getErrorMessage(error))
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Campaign Launch Studio</h1>
        <p className={styles.subtitle}>Create, schedule and publish polished Meta campaigns from one workspace.</p>
      </div>

      {/* Input Form */}
      <div className={styles.formCard}>
        <h2 className={styles.sectionTitle}>Campaign Setup</h2>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label className={styles.label}>Product / Business</label>
            <input className={styles.input} value={product}
              onChange={e => setProduct(e.target.value)} placeholder="e.g. SolarEdge Panel Cleaning" />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Location</label>
            <input className={styles.input} value={location}
              onChange={e => setLocation(e.target.value)} placeholder="e.g. Bangalore, India" />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Campaign Goal</label>
            <select className={styles.select} value={goal} onChange={e => setGoal(e.target.value)}>
              <option>Increase Sales</option>
              <option>Brand Awareness</option>
              <option>Lead Generation</option>
              <option>App Installs</option>
              <option>Website Traffic</option>
            </select>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Tone</label>
            <select className={styles.select} value={tone} onChange={e => setTone(e.target.value)}>
              <option>Professional</option>
              <option>Exciting</option>
              <option>Friendly</option>
              <option>Urgent</option>
              <option>Inspirational</option>
            </select>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Destination URL</label>
            <input className={styles.input} value={linkUrl}
              onChange={e => setLinkUrl(e.target.value)} placeholder="https://yourwebsite.com" />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Daily Budget (₹)</label>
            <input className={styles.input} type="number" value={dailyBudget}
              onChange={e => setDailyBudget(Number(e.target.value))} />
          </div>
          <div className={styles.schedulePanel}>
            <label className={styles.scheduleToggle}>
              <input type="checkbox" checked={autoSchedule} onChange={e => setAutoSchedule(e.target.checked)} />
              Auto schedule
            </label>
            <input className={styles.input} type="datetime-local" value={scheduledTime}
              onChange={e => setScheduledTime(e.target.value)} disabled={!autoSchedule} min={toDatetimeLocalValue(new Date())} />
          </div>
        </div>

        <div className={styles.actions}>
          <button className={styles.btnPrimary} onClick={generateCampaign} disabled={loading}>
            {loading ? 'Generating...' : 'Generate Campaign'}
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {message && <div className={styles.success}>✅ {message}</div>}
      {errorMessage && <div className={styles.error}>❌ {errorMessage}</div>}

      {/* Generated Campaign Result */}
      {result && (
        <div className={styles.resultCard}>
          <div className={styles.resultHeader}>
            <h2 className={styles.sectionTitle}>Campaign Preview</h2>
            {savedId && <span className={styles.savedBadge}>✓ Saved</span>}
          </div>

          <div className={styles.resultGrid}>
            <div className={styles.resultItem}>
              <span className={styles.resultLabel}>Product</span>
              <span className={styles.resultValue}>{result.product}</span>
            </div>
            <div className={styles.resultItem}>
              <span className={styles.resultLabel}>Platform</span>
              <span className={styles.resultValue}>{result.platform || 'Facebook & Instagram'}</span>
            </div>
            <div className={styles.resultItem}>
              <span className={styles.resultLabel}>Recommended Budget</span>
              <span className={styles.resultValue}>{result.budget || '—'}</span>
            </div>
            <div className={styles.resultItem}>
              <span className={styles.resultLabel}>AI Score</span>
              <span className={styles.scoreBadge}>{result.performance_score}/100</span>
            </div>
          </div>

          <div className={styles.copySection}>
            <div className={styles.copyBlock}>
              <span className={styles.resultLabel}>Headline</span>
              <p className={styles.headline}>{result.headline}</p>
            </div>
            <div className={styles.copyBlock}>
              <span className={styles.resultLabel}>Primary Text</span>
              <p className={styles.primaryText}>{result.primary_text}</p>
            </div>
            <div className={styles.copyGrid}>
              <div className={styles.copyBlock}>
                <span className={styles.resultLabel}>Call to Action</span>
                <span className={styles.ctaBadge}>{result.call_to_action}</span>
              </div>
              <div className={styles.copyBlock}>
                <span className={styles.resultLabel}>Target Audience</span>
                <p className={styles.resultValue}>{typeof result.audience === 'object' ? JSON.stringify(result.audience) : result.audience}</p>
              </div>
            </div>
            <div className={styles.copyBlock}>
              <span className={styles.resultLabel}>Strategy</span>
              <p className={styles.resultValue}>{result.strategy}</p>
            </div>
          </div>

          {/* Poster Image Upload */}
          <div className={styles.field} style={{ marginTop: '1rem' }}>
            <label className={styles.label}>
              Poster Image <span className={styles.optional}>(optional — upload your poster or open Poster Studio)</span>
            </label>
            <div className={styles.posterRow}>
              <label className={styles.btnUpload}>
                {uploading ? 'Uploading...' : imageFileName ? `📎 ${imageFileName}` : '📁 Upload Image'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  style={{ display: 'none' }}
                  onChange={handleImageUpload}
                  disabled={uploading}
                />
              </label>
              <button className={styles.btnSecondary} onClick={openPosterStudio}>
                Open Poster Studio
              </button>
            </div>
            {imageUrl && (
              <p className={styles.imageUrlHint} title={imageUrl}>
                ✅ Image ready: <span>{imageUrl.split('/').pop()}</span>
              </p>
            )}
          </div>

          {imageUrl && (
            <img src={imageUrl} alt="Campaign poster" className={styles.posterPreview}
              onError={() => setErrorMessage('Could not load image from URL')} />
          )}

          {/* Action Buttons */}
          <div className={styles.campaignActions}>
            <button className={styles.btnSave} onClick={saveCampaign} disabled={saving || !!savedId}>
              {saving ? 'Saving...' : savedId ? 'Saved' : 'Save Campaign'}
            </button>
            <button className={styles.btnPublish} onClick={publishCampaign} disabled={publishing}>
              {publishing ? 'Publishing...' : autoSchedule ? 'Schedule on Meta' : 'Publish to Meta'}
            </button>
            <button className={styles.btnRetry} onClick={retryCreateAd} disabled={publishing}
              title="Use after authenticating your Facebook account in Ads Manager">
              {publishing ? 'Retrying...' : '🔁 Retry Ad Creation'}
            </button>
            <button className={styles.btnDelete} onClick={deleteCampaign} disabled={!savedId && !result?.id}>
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
